import axios from "axios";
import CryptoJS from "crypto-js";

const API_URL = "https://seltentechnologiesdemo.com/moona-backend/service/v4_1/rest.php";
const MODULE_NAME = "keys_passwords";
const INPUT_TYPE = "JSON";
const RESPONSE_TYPE = "JSON";

// Encryption key for passkey data (should match backend or be environment-specific)
const ENCRYPTION_KEY = process.env.REACT_APP_ENCRYPTION_KEY || "your-secret-encryption-key-2024";

/**
 * Encrypt passkey data using AES encryption
 * @param {string} data - The data to encrypt (PIN/Password)
 * @returns {string} - Encrypted data in Base64 format
 */
export const encryptPasskeyData = (data) => {
  if (!data) return "";
  try {
    const encrypted = CryptoJS.AES.encrypt(data, ENCRYPTION_KEY).toString();
    return encrypted;
  } catch (error) {
    console.error("❌ Error encrypting passkey data:", error);
    return data; // Return original if encryption fails
  }
};

/**
 * Decrypt passkey data
 * @param {string} encryptedData - The encrypted data
 * @returns {string} - Decrypted data
 */
export const decryptPasskeyData = (encryptedData) => {
  if (!encryptedData) return "";
  try {
    const decrypted = CryptoJS.AES.decrypt(encryptedData, ENCRYPTION_KEY);
    return decrypted.toString(CryptoJS.enc.Utf8);
  } catch (error) {
    console.error("❌ Error decrypting passkey data:", error);
    return encryptedData; // Return encrypted if decryption fails
  }
};

/**
 * Hash PIN using SHA-256 (for storage)
 * @param {string} pin - The PIN/Password to hash
 * @returns {string} - Hashed PIN
 */
export const hashPin = (pin) => {
  if (!pin) return "";
  try {
    const hashed = CryptoJS.SHA256(pin).toString();
    console.log("🔐 PIN hashed successfully");
    return hashed;
  } catch (error) {
    console.error("❌ Error hashing PIN:", error);
    return pin;
  }
};

/**
 * Fetch passkey details by parent ID
 * @param {string} sessionId - The session ID
 * @param {string} parentId - The parent user ID
 * @returns {Promise<Array>} - Array of passkey entries
 */
/**
 * Helper function to handle retries
 * @param {Function} fn - Function to retry
 * @param {number} retries - Number of retries
 * @param {number} delay - Delay between retries in ms
 */
const withRetry = async (fn, retries = 3, delay = 1000) => {
  try {
    return await fn();
  } catch (error) {
    if (retries > 0) {
      await new Promise(resolve => setTimeout(resolve, delay));
      return withRetry(fn, retries - 1, delay);
    }
    throw error;
  }
};

export const fetchPasskeyDetailsByParent = async (sessionId, parentId) => {
  console.log(`🔍 [PasskeyService] Fetching passkeys for parent: ${parentId}`);

  // Validate inputs - return empty array instead of throwing
  if (!sessionId || !parentId) {
    console.warn("⚠️ [PasskeyService] Missing sessionId or parentId");
    return [];
  }

  try {
    // Fetch all entries without query (like bankDetailsService)
    // This avoids the 500 error that occurs with query parameters
    const rest_data = {
      session: sessionId,
      module_name: MODULE_NAME,
    };

    const fullUrl = `${API_URL}?method=get_entry_list&input_type=${INPUT_TYPE}&response_type=${RESPONSE_TYPE}&rest_data=${encodeURIComponent(JSON.stringify(rest_data))}`;
    console.log("📤 [PasskeyService] Request URL:", fullUrl);

    const response = await axios.get(fullUrl);

    console.log("📡 [PasskeyService] API Response:", response.data);

    // Check for invalid session
    if (response?.data?.name === "Invalid Session ID" || response?.data?.number) {
      console.warn("⚠️ [PasskeyService] Invalid session");
      return [];
    }

    // Check for module doesn't exist error
    if (response?.data?.description && response.data.description.includes("does not exist")) {
      console.warn("⚠️ [PasskeyService] Module does not exist");
      return [];
    }

    const entryList = response?.data?.entry_list || [];
    
    if (!entryList.length) {
      console.log("ℹ️ [PasskeyService] No passkeys found");
      return [];
    }

    // Process the entries
    const processedData = entryList.map((entry) => {
      const nameValueList = entry.name_value_list;
      const encryptedPin = nameValueList.pin?.value || "";
      
      return {
        id: nameValueList.id?.value || "",
        name: nameValueList.name?.value || "",
        type: nameValueList.type?.value || "",
        description: nameValueList.description?.value || "",
        pin: encryptedPin ? decryptPasskeyData(encryptedPin) : "", // Decrypt the PIN for display
        parentid: nameValueList.parentid?.value || "",
        deleted: nameValueList.deleted?.value || "0",
      };
    });

    console.log("📦 [PasskeyService] Processed data:", processedData);

    // Filter by parentId on client side (like bankDetailsService)
    const filteredData = processedData.filter(
      (entry) =>
        (entry.parentid === parentId || entry.parentid === String(parentId)) &&
        entry.deleted === "0"
    );

    console.log(`✅ [PasskeyService] Found ${filteredData.length} passkey(s) for parent ${parentId}`);
    return filteredData;
  } catch (error) {
    console.error("❌ [PasskeyService] Error fetching passkeys:", error);
    
    // Enhanced error logging
    if (error.response) {
      console.error("Error response data:", error.response.data);
      console.error("Error response status:", error.response.status);
      console.error("Error response headers:", error.response.headers);
    }
    
    // Return empty array on error (like bankDetailsService)
    if (error.response?.status === 500) {
      console.warn("⚠️ [PasskeyService] Server error, returning empty array");
      return [];
    }
    
    return [];
  }
};

/**
 * Add a new passkey entry
 * @param {string} sessionId - The session ID
 * @param {Object} passkeyData - The passkey data
 * @returns {Promise<Object>} - The created passkey entry
 */
export const addPasskeyData = async (sessionId, passkeyData) => {
  console.log("➕ [PasskeyService] Adding new passkey:", passkeyData);

  try {
    // Encrypt the PIN before storing (so it can be decrypted later)
    const encryptedPin = encryptPasskeyData(passkeyData.pin);
    console.log('[PasskeyService] Saving passkey with parentid:', passkeyData.parentid);

    const restData = {
      session: sessionId,
      module_name: "keys_passwords",
      name_value_list: [
        { name: "name", value: passkeyData.name || "" },
        { name: "type", value: passkeyData.type || "" },
        { name: "description", value: passkeyData.description || "" },
        { name: "pin", value: encryptedPin },
        { name: "parentid", value: passkeyData.parentid || "" },
      ],
    };

    const response = await axios.post(
      API_URL,
      new URLSearchParams({
        method: "set_entry",
        input_type: "JSON",
        response_type: "JSON",
        rest_data: JSON.stringify(restData),
      }),
      {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
      }
    );

    console.log("📡 [PasskeyService] Add response:", response.data);
    
    if (response.data.id) {
      console.log(`✅ [PasskeyService] Passkey added successfully with ID: ${response.data.id}`);
    } else {
      console.log("✅ [PasskeyService] Passkey added successfully");
    }
    
    return response.data;
  } catch (error) {
    console.error("❌ [PasskeyService] Error adding passkey:", error);
    if (error.response) {
      console.error("Error response:", error.response.data);
    }
    throw error;
  }
};

/**
 * Edit an existing passkey entry
 * @param {string} sessionId - The session ID
 * @param {string} passkeyId - The passkey ID
 * @param {Object} passkeyData - The updated passkey data
 * @returns {Promise<Object>} - The updated passkey entry
 */
export const editPasskeyData = async (sessionId, passkeyId, passkeyData) => {
  console.log(`✏️ [PasskeyService] Editing passkey ${passkeyId}:`, passkeyData);

  try {
    // Encrypt the PIN if it's being updated (so it can be decrypted later)
    const encryptedPin = passkeyData.pin ? encryptPasskeyData(passkeyData.pin) : undefined;

    const nameValueList = [
      { name: "id", value: passkeyId },
      { name: "name", value: passkeyData.name || "" },
      { name: "type", value: passkeyData.type || "" },
      { name: "description", value: passkeyData.description || "" },
      { name: "parentid", value: passkeyData.parentid || "" },
    ];

    // Only include PIN if it's provided (user wants to change it)
    if (encryptedPin) {
      nameValueList.push({ name: "pin", value: encryptedPin });
    }

    const restData = {
      session: sessionId,
      module_name: "keys_passwords",
      name_value_list: nameValueList,
    };

    const response = await axios.post(
      API_URL,
      new URLSearchParams({
        method: "set_entry",
        input_type: "JSON",
        response_type: "JSON",
        rest_data: JSON.stringify(restData),
      }),
      {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
      }
    );

    console.log("📡 [PasskeyService] Edit response:", response.data);
    
    if (response.data.id) {
      console.log(`✅ [PasskeyService] Passkey ${passkeyId} updated successfully`);
    } else {
      console.log(`✅ [PasskeyService] Passkey updated successfully`);
    }
    
    return response.data;
  } catch (error) {
    console.error("❌ [PasskeyService] Error editing passkey:", error);
    if (error.response) {
      console.error("Error response:", error.response.data);
    }
    throw error;
  }
};

/**
 * Delete a passkey entry (soft delete)
 * @param {string} sessionId - The session ID
 * @param {string} passkeyId - The passkey ID to delete
 * @returns {Promise<void>}
 */
export const deletePasskeyData = async (sessionId, passkeyId) => {
  console.log(`🗑️ [PasskeyService] Deleting passkey ${passkeyId}`);

  try {
    const restData = {
      session: sessionId,
      module_name: "keys_passwords",
      name_value_list: [
        { name: "id", value: passkeyId },
        { name: "deleted", value: "1" },
      ],
    };

    const response = await axios.post(
      API_URL,
      new URLSearchParams({
        method: "set_entry",
        input_type: "JSON",
        response_type: "JSON",
        rest_data: JSON.stringify(restData),
      }),
      {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
      }
    );

    console.log("📡 [PasskeyService] Delete response:", response.data);

    if (response.data.id) {
      console.log(`✅ [PasskeyService] Passkey ${passkeyId} deleted successfully`);
      return;
    }

    throw new Error("Failed to delete passkey");
  } catch (error) {
    console.error("❌ [PasskeyService] Error deleting passkey:", error);
    if (error.response) {
      console.error("Error response:", error.response.data);
    }
    throw error;
  }
};

/**
 * Sync passkey entries (used in profile service)
 * @param {string} sessionId - The session ID
 * @param {string} userId - The user ID
 * @param {Array} passkeys - Array of passkey data from form
 * @returns {Promise<void>}
 */
export const syncPasskeyEntries = async (sessionId, userId, passkeys) => {
  console.log("🔄 [PasskeyService] Syncing passkey entries for user:", userId);

  try {
    // Fetch existing passkeys
    const existingPasskeys = await fetchPasskeyDetailsByParent(sessionId, userId);
    const existingIds = existingPasskeys.map((pk) => pk.id);

    // Process each passkey from the form
    for (const passkey of passkeys) {
      if (passkey.id && existingIds.includes(passkey.id)) {
        // Update existing passkey
        await editPasskeyData(sessionId, passkey.id, {
          ...passkey,
          parentid: userId,
        });
      } else {
        // Add new passkey
        await addPasskeyData(sessionId, {
          ...passkey,
          parentid: userId,
        });
      }
    }

    console.log("✅ [PasskeyService] Passkey sync completed");
  } catch (error) {
    console.error("❌ [PasskeyService] Error syncing passkeys:", error);
    throw error;
  }
};
